import { Suspense, useEffect, useMemo, useRef } from 'react';
import { Await } from 'react-router';
import root from 'react-shadow';

import type { FooterTemplate } from '@jane/bloom/routes/api.footer/types';

import styles from './shadowFooter.module.css';
import { ShadowStylesheets, buildStylesheets } from './stylesheets';

/**
 * Creates a new style element and appends it to the head of the document with the font faces.
 * Currently, headers don't inherit the font families from the shadow DOM,
 * so we need to append a style element to the head of the document with the font faces.
 * @see https://github.com/mdn/interactive-examples/issues/887
 */
const embedHeadStyles = (content: string) => {
  if (content) {
    const style = document.createElement('style');
    style.textContent = content;
    document.head.appendChild(style);
  }
};

export const ShadowFooter = ({
  content,
  contentOverrides,
  externalStylesheetUrls,
  headContent,
}: FooterTemplate) => {
  const ref = useRef<HTMLDivElement>(null);
  const stylesheets = useMemo(
    () => buildStylesheets(externalStylesheetUrls),
    [externalStylesheetUrls]
  );

  useEffect(() => {
    embedHeadStyles(headContent);
  }, [headContent]);

  if (!content && !contentOverrides) {
    return null;
  }

  return (
    // @ts-expect-error - ignore react-shadow element types
    <root.section ref={ref} className={styles['footer']}>
      <Suspense>
        <Await resolve={stylesheets}>
          {(sheets) => <ShadowStylesheets rootRef={ref} styleSheets={sheets} />}
        </Await>
      </Suspense>

      <div
        dangerouslySetInnerHTML={{
          __html: `${content}${contentOverrides}`,
        }}
      />
      {/* @ts-expect-error - ignore react-shadow element types */}
    </root.section>
  );
};
